import {
  parseAddressBookCsv,
  type ParsedAddressBookCsv,
} from './parse-address-book-csv';
import type { EditAddressBookInput } from '../../types/address-book';

type AddressBookCsvReadyFileState = {
  status: 'ready';
  fileName: string;
  rowCount: number;
  instructions: EditAddressBookInput['instructions'];
};

type AddressBookCsvFailedFileState = {
  status: 'failed';
  fileName: string;
  errorMessage: string;
};

export type AddressBookCsvFileState =
  | AddressBookCsvReadyFileState
  | AddressBookCsvFailedFileState;

/**
 * Converts CSV import instructions into the endpoint payload expected by editAddressBook.
 */
export function buildEditAddressBookInput(
  instructions: EditAddressBookInput['instructions']
): EditAddressBookInput {
  return {
    instructions,
  };
}

/**
 * Converts parsed CSV rows into the endpoint payload expected by editAddressBook.
 */
export function buildEditAddressBookInputFromParsedCsv(
  parsedCsv: ParsedAddressBookCsv
): EditAddressBookInput {
  return buildEditAddressBookInput(parsedCsv.instructions);
}

/**
 * Parses raw CSV text and converts it into the endpoint payload for bulk import.
 */
export function buildEditAddressBookInputFromCsv(
  csvText: string
): EditAddressBookInput {
  return buildEditAddressBookInputFromParsedCsv(parseAddressBookCsv(csvText));
}

/**
 * Builds the dialog file state for a successfully parsed CSV file.
 */
export function buildReadyAddressBookCsvFileState(
  fileName: string,
  csvText: string
): AddressBookCsvReadyFileState {
  const parsedCsv = parseAddressBookCsv(csvText);
  const input = buildEditAddressBookInputFromParsedCsv(parsedCsv);

  return {
    status: 'ready',
    fileName,
    rowCount: parsedCsv.rowCount,
    instructions: input.instructions,
  };
}

/**
 * Builds the dialog file state for a CSV file that failed validation or parsing.
 */
export function buildFailedAddressBookCsvFileState(
  fileName: string,
  error: unknown
): AddressBookCsvFailedFileState {
  return {
    status: 'failed',
    fileName,
    errorMessage:
      error instanceof Error
        ? error.message
        : 'Failed to parse the selected CSV file.',
  };
}

/**
 * Builds the dialog file state for a CSV file, preserving a failed state when
 * parsing or validation throws.
 */
export function buildAddressBookCsvFileState(
  fileName: string,
  csvText: string
): AddressBookCsvFileState {
  try {
    return buildReadyAddressBookCsvFileState(fileName, csvText);
  } catch (error) {
    return buildFailedAddressBookCsvFileState(fileName, error);
  }
}
